import {
    Alert,
    Box,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Tooltip,
    useTheme,
} from '@mui/material';
import { useFormikContext } from 'formik';
import { CheckboxWithLabel } from 'formik-mui';
import { MdAdd, MdReport } from 'react-icons/md';
import { useSelector } from 'react-redux';
import useResizeObserver from 'use-resize-observer';

import { useDragEnd } from 'components/ProcedureDesigner/useDragEnd';
import { FormTextField } from 'components/TextField';
import { contentService } from 'features/services';
import { makeFormStyles } from 'forms/styles';
import ProcedureFormNode from 'GeminiViewerComponent/components/Procedure/components/ProcedureFormNode';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { ExcludeFromNavigationField, OverrideShowResultContentField } from '../../CommonNodeFormFields';
import { FormNodeFieldsEditor } from '../../FormNodeEditor/FormNodeFieldsEditor';
import ButtonAnswerGroupType from '../ButtonAnswerGroupType';
import HotspotAnswerGroupType from '../HotspotAnswerGroupType';
import ImageAnswerGroupType from '../ImageAnswerGroupType';
import SelectAnswerGroupType from '../SelectAnswerGroupType';
import { makeContentNodeFormStyles } from '../styles';

const ContentNodeFormFields = ({
    nodes,
    openEditFieldForm,
    setOpenEditFieldForm,
    selectedField,
    isValidVariableName,
    checkUniqueVariableName,
}) => {
    const theme = useSelector(selectDefaultTheme);
    const nodeFormStyles = makeContentNodeFormStyles(theme);
    const { values, setFieldValue } = useFormikContext();

    return (
        <Box display="flex" width="auto" flex="1 1 0px">
            <Box
                component="fieldset"
                border="1px solid #cccccc"
                borderRadius="5px"
                padding="5px"
                width="100%"
                style={{
                    minInlineSize: 'inherit',
                    overflow: 'auto',
                }}
            >
                <Box component="legend" marginLeft="10px" padding="3px" fontSize="14px" color="#6d7178">
                    Form Fields
                </Box>
                <Box marginBottom="5px">
                    <Tooltip title="Add Form field">
                        <span>
                            <IconButton
                                className={nodeFormStyles.addButtonIcon}
                                onClick={() => setOpenEditFieldForm(true)}
                                size="small"
                            >
                                <MdAdd className="react-icon" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
                <FormNodeFieldsEditor
                    node={values}
                    nodes={values.fields}
                    procedureNodes={nodes}
                    setFieldValue={setFieldValue}
                    openEditFieldForm={openEditFieldForm}
                    setOpenEditFieldForm={setOpenEditFieldForm}
                    isValidVariableName={isValidVariableName}
                    checkUniqueVariableName={checkUniqueVariableName}
                    selectedField={selectedField?.id}
                />
            </Box>
        </Box>
    );
};

const ContentNodeFormPreview = ({ procedureRef, selectedField, setSelectedField }) => {
    const { values } = useFormikContext();
    const muiTheme = useTheme();
    const smWidth = muiTheme.breakpoints.values.sm;
    const { width: formWidth } = useResizeObserver({ ref: procedureRef });

    return (
        <Box width="auto" display="flex" marginTop="5px">
            <Box
                component="fieldset"
                border="1px solid #cccccc"
                borderRadius="5px"
                width="100%"
                ref={procedureRef}
                style={{
                    minInlineSize: 'inherit',
                    overflow: 'auto',
                }}
            >
                <Box component="legend" marginLeft="10px" padding="3px" fontSize="14px" color="#6d7178">
                    Form Preview
                </Box>
                <ProcedureFormNode
                    formFields={values.fields}
                    isSubmitAllow={false}
                    isMobileWidth={formWidth < smWidth}
                    selectedField={selectedField}
                    setSelectedField={setSelectedField}
                    contentService={contentService}
                />
            </Box>
        </Box>
    );
};

const ContentNodeFormFailureDetails = () => {
    return (
        <Box component="fieldset" border="1px solid #cccccc" borderRadius="5px">
            <Box component="legend" marginLeft="10px" padding="3px" fontSize="14px" color="#6d7178">
                Procedure Failure Target Details
            </Box>
            <Box display="flex" alignItems="center" flexDirection="column" gap="5px" width="100%">
                <FormControl sx={{ padding: '0 8px' }} fullWidth>
                    <FormTextField label="Title" name="target_title" placeholder="Target Title" />
                </FormControl>
                <FormControl sx={{ padding: '0 8px' }} fullWidth>
                    <FormTextField label="Details" name="target_details" placeholder="Target Details" />
                </FormControl>
                <FormControl sx={{ padding: '0 8px' }} fullWidth>
                    <FormTextField label="URL" name="target_url" placeholder="Target URL" />
                </FormControl>
            </Box>
        </Box>
    );
};

const ContentNodeFormSettings = ({ selectCompletionNode, completionNode }) => {
    const theme = useSelector(selectDefaultTheme);
    const commonFormStyles = makeFormStyles(theme);
    const { values, setFieldValue } = useFormikContext();

    return (
        <Box component="fieldset" border="1px solid #cccccc" borderRadius="5px">
            <Box component="legend" marginLeft="10px" padding="3px" fontSize="14px" color="#6d7178">
                Settings
            </Box>
            <FormControl sx={{ padding: '0 8px' }} fullWidth>
                <FormTextField
                    component={CheckboxWithLabel}
                    Label={{
                        label: 'Image attachments',
                    }}
                    label="Image attachments"
                    type="checkbox"
                    checked={Boolean(values.image_attachments_allow)}
                    onChange={(evt) => setFieldValue('image_attachments_allow', evt.target.checked)}
                />
            </FormControl>
            <FormControl sx={{ padding: '0 8px' }} fullWidth>
                <FormTextField
                    component={CheckboxWithLabel}
                    Label={{
                        label: 'Completion (submit button)',
                    }}
                    label="Completion (submit button)"
                    type="checkbox"
                    checked={Boolean(values.completion)}
                    onChange={(evt) => {
                        selectCompletionNode(evt.target.checked, setFieldValue);
                    }}
                />
            </FormControl>
            <FormControl sx={{ padding: '0 8px' }} fullWidth>
                <ExcludeFromNavigationField />
            </FormControl>
            <FormControl sx={{ padding: '0 8px' }} fullWidth>
                <OverrideShowResultContentField sc={{ padding: '0 8px' }} fullWidth />
            </FormControl>
            {completionNode && (
                <Box className={commonFormStyles.inputErr}>
                    Node {completionNode?.id} - {completionNode?.title} is already completion node.
                </Box>
            )}
        </Box>
    );
};

const ContentNodeFormPreset = () => {
    const { values, setFieldValue } = useFormikContext();

    return (
        <Box component="fieldset" border="1px solid #cccccc" borderRadius="5px">
            <Box component="legend" marginLeft="10px" padding="3px" fontSize="14px" color="#6d7178">
                Presets
            </Box>
            <Box display="flex" alignItems="center" sx={{ px: 2 }}>
                <FormTextField
                    component={CheckboxWithLabel}
                    Label={{
                        label: 'Next button',
                    }}
                    label="Next button"
                    type="checkbox"
                    checked={values.next_button}
                    onChange={(evt) => setFieldValue('next_button', evt.target.checked)}
                />
                <FormTextField
                    component={CheckboxWithLabel}
                    Label={{
                        label: 'Back button',
                    }}
                    label="Back button"
                    type="checkbox"
                    checked={values.back_button}
                    onChange={(evt) => setFieldValue('back_button', evt.target.checked)}
                />
            </Box>
            <Box>
                {values.next_button &&
                    ((values.answer_group === 'button' && values.option_buttons && values.option_buttons.length > 0) ||
                        (values.answer_group === 'image' && values.image_options && values.image_options?.length > 0) ||
                        (values.answer_group === 'select' &&
                            values.select_options &&
                            values.select_options?.length > 0) ||
                        (values.answer_group === 'hotspot' &&
                            values.hotspot_options &&
                            values.hotspot_options?.length > 0)) && (
                        <Alert icon={<MdReport />} severity="warning" sx={{ m: 1 }}>
                            If you have answer options, the “Next” button will be hidden from users until they select an
                            answer and return to this node.
                        </Alert>
                    )}
            </Box>
        </Box>
    );
};

const ContentNodeFormAnswerOptions = () => {
    const { values, setFieldValue } = useFormikContext();

    return (
        <>
            <Box component="legend" padding="3px" fontSize="14px" color="#6d7178">
                Answers
            </Box>
            <FormLabel>Answer group type</FormLabel>
            <RadioGroup
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                }}
                name="answer_group"
                value={values?.answer_group}
                onChange={(e) => {
                    setFieldValue('answer_group', e.target.value);
                    setFieldValue(
                        'multiple_answers_allow',
                        e.target.value === 'button' ? false : Boolean(values?.multiple_answers_allow)
                    );
                }}
            >
                <FormControlLabel value="button" control={<Radio color="primary" />} label="Button" />
                <FormControlLabel value="image" control={<Radio color="primary" />} label="Image" />
                <FormControlLabel value="select" control={<Radio color="primary" />} label="Select" />
                <FormControlLabel value="hotspot" control={<Radio color="primary" />} label="Hotspot" />
            </RadioGroup>
        </>
    );
};

const ContentNodeFormAnswerOptionFields = ({ node, nodes }) => {
    const { values, setFieldValue } = useFormikContext();
    const theme = useSelector(selectDefaultTheme);
    const nodeFormStyles = makeContentNodeFormStyles(theme);
    const [onDragEnd, handleRemove] = useDragEnd();
    const commonFormStyles = makeFormStyles(theme);

    const handleRemoveOptionsSelected = (opt_txt, values, fieldName, setFieldValue) => {
        let dataValues = [...values];
        let filteredData = dataValues.map((data) => {
            return {
                ...data,
                options_required: data.options_required.filter((opt) => opt.option_text !== opt_txt),
            };
        });
        setFieldValue(fieldName, filteredData);
    };

    return (
        <>
            {values?.answer_group !== 'button' && (
                <>
                    <FormLabel>Multiple answers allowed?</FormLabel>
                    <RadioGroup
                        style={{ display: 'flex', flexDirection: 'row' }}
                        name="multiple_answers_allow"
                        value={values?.multiple_answers_allow === true ? 'yes' : 'no'}
                        onChange={(e) => {
                            setFieldValue('multiple_answers_allow', e.target.value === 'yes' ? true : false);
                        }}
                    >
                        <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
                        <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
                    </RadioGroup>
                </>
            )}
            {values?.answer_group === 'hotspot' && (
                <>
                    <FormLabel>Show hotspots?</FormLabel>
                    <RadioGroup
                        style={{ display: 'flex', flexDirection: 'row' }}
                        name="show_hotspot"
                        value={values?.show_hotspot === false || values?.show_hotspot === undefined ? 'no' : 'yes'}
                        onChange={(e) => {
                            setFieldValue('show_hotspot', e.target.value === 'no' ? false : true);
                            if (e.target.value === 'no') {
                                setFieldValue('show_hotspot_label', false);
                            }
                        }}
                    >
                        <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
                        <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
                    </RadioGroup>

                    {values?.show_hotspot && (
                        <>
                            <FormLabel>Show Labels? (Only if Yes is selected on the previous question)</FormLabel>
                            <RadioGroup
                                style={{ display: 'flex', flexDirection: 'row' }}
                                name="show_hotspot_label"
                                value={
                                    values?.show_hotspot_label === false || values?.show_hotspot_label === undefined
                                        ? 'no'
                                        : 'yes'
                                }
                                onChange={(e) => {
                                    setFieldValue('show_hotspot_label', e.target.value === 'no' ? false : true);
                                }}
                            >
                                <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
                                <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
                            </RadioGroup>
                        </>
                    )}

                    <FormLabel>
                        Navigate to item? (determines if the view navigates to zone and item, or does nothing)
                    </FormLabel>
                    <RadioGroup
                        style={{ display: 'flex', flexDirection: 'row' }}
                        name="navigate_to_item"
                        value={
                            values?.navigate_to_item === false || values?.navigate_to_item === undefined ? 'no' : 'yes'
                        }
                        onChange={(e) => {
                            setFieldValue('navigate_to_item', e.target.value === 'no' ? false : true);
                        }}
                    >
                        <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
                        <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
                    </RadioGroup>

                    <FormControl sx={{ padding: '0 8px' }} fullWidth>
                        <FormTextField
                            label="Message for incorrect hotspot selection"
                            name="incorrect_hotspot_message"
                            placeholder="Enter message shown when an incorrect hotspot is selected"
                        />
                    </FormControl>

                    <FormControl sx={{ padding: '0 8px' }} fullWidth>
                        <FormTextField
                            label="Maximum retry attempts"
                            name="max_hotspot_retry_count"
                            placeholder="Enter maximum number of retries"
                            type="number"
                        />
                    </FormControl>

                    <FormControl sx={{ padding: '0 8px', marginTop: '8px' }} fullWidth>
                        <InputLabel id="target-node-id">Incorrect Target Node</InputLabel>
                        <Select
                            className={commonFormStyles.materialSelect}
                            style={{ margin: '0' }}
                            labelId="target-node-id"
                            margin="dense"
                            id="incorrect_target_node"
                            label="Incorrect Target Node"
                            placeholder="Incorrect Target Node"
                            value={values?.incorrect_target_node?.action?.id ?? ''}
                            onChange={(e) => {
                                const curr = {
                                    ...values.incorrect_target_node,
                                    action: {
                                        id: e.target.value,
                                        type: 'node',
                                    },
                                };
                                setFieldValue('incorrect_target_node', curr);
                            }}
                        >
                            <MenuItem key={'next'} value={'next'}>
                                Next
                            </MenuItem>
                            <MenuItem key={'back'} value={'back'}>
                                Back
                            </MenuItem>
                            {nodes?.length > 0 &&
                                nodes.map((nd) => (
                                    <MenuItem key={nd?.id} value={nd?.id}>
                                        {nd?.title} - #{nd?.id}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </>
            )}
            {values?.answer_group === 'button' && (
                <ButtonAnswerGroupType node={node} nodes={nodes} values={values} setFieldValue={setFieldValue} />
            )}
            {values?.answer_group === 'select' && (
                <SelectAnswerGroupType
                    node={node}
                    nodes={nodes}
                    values={values}
                    setFieldValue={setFieldValue}
                    nodeFormStyles={nodeFormStyles}
                    onDragEnd={onDragEnd}
                    handleRemove={handleRemove}
                    handleRemoveOptionsSelected={handleRemoveOptionsSelected}
                />
            )}
            {values?.answer_group === 'image' && (
                <ImageAnswerGroupType
                    node={node}
                    nodes={nodes}
                    values={values}
                    setFieldValue={setFieldValue}
                    nodeFormStyles={nodeFormStyles}
                    onDragEnd={onDragEnd}
                    handleRemove={handleRemove}
                    handleRemoveOptionsSelected={handleRemoveOptionsSelected}
                />
            )}
            {values?.answer_group === 'hotspot' && (
                <HotspotAnswerGroupType
                    node={node}
                    nodes={nodes}
                    values={values}
                    setFieldValue={setFieldValue}
                    nodeFormStyles={nodeFormStyles}
                    onDragEnd={onDragEnd}
                    handleRemove={handleRemove}
                    handleRemoveOptionsSelected={handleRemoveOptionsSelected}
                />
            )}
        </>
    );
};

export {
    ContentNodeFormAnswerOptionFields,
    ContentNodeFormAnswerOptions,
    ContentNodeFormFailureDetails,
    ContentNodeFormFields,
    ContentNodeFormPreset,
    ContentNodeFormPreview,
    ContentNodeFormSettings,
};
